import React from "react";
import { motion } from "framer-motion";
import { 
  Recycle, 
  Zap, 
  Droplets, 
  Wind, 
  ShieldCheck,
  ChevronRight,
  ArrowRight 
} from 'lucide-react';
import { IMAGES } from '@/assets/images';
import { ROUTE_PATHS } from '@/lib/index';
import { Button } from '@/components/ui/button';
import { springPresets, fadeInUp, staggerContainer, staggerItem } from '@/lib/motion';

export default function SustainabilityEn() {
  const sustainabilityPillarsEn = [
    {
      icon: <Recycle className="w-10 h-10 text-primary" />,
      title: "Circular Economy",
      description: "Aluminum is 100% recyclable metal. We protect natural resources by increasing scrap recovery to 95% level in our production processes.",
    },
    {
      icon: <Zap className="w-10 h-10 text-primary" />,
      title: "Energy Efficiency",
      description: "With smart automation systems used in our extrusion lines and furnaces, we reduced energy consumption by 30% in line with our 2026 targets.",
    },
    {
      icon: <Wind className="w-10 h-10 text-primary" />,
      title: "Carbon Footprint",
      description: "We minimize our carbon emissions with our green energy certified supply chain and solar panels on our factory roofs.",
    },
    {
      icon: <Droplets className="w-10 h-10 text-primary" />,
      title: "Water Management",
      description: "We aim to reduce water waste to zero by using closed-loop water treatment systems in our surface treatment and foundry facilities.",
    },
  ];

  const initiativesEn = [
    {
      title: "Recycling and Foundry",
      content: "A large portion of the aluminum billets we process in our own foundry is obtained by reprocessing high-quality scraps from our factory and collected from the market. This process requires 95% less energy compared to primary aluminum production.",
      image: IMAGES.SUSTAINABILITY_3,
    },
  ];

  return (
    <div className="flex flex-col w-full">
      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden bg-primary">
        <img 
          src={IMAGES.SUSTAINABILITY_1} 
          alt="Sustainability" 
          className="absolute inset-0 object-cover w-full h-full opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-primary/60 via-transparent to-background" />
        
        <div className="container relative z-10 mx-auto px-4 text-center">
          <motion.h1 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-5xl font-bold mb-6 text-white"
          >
            Sustainability
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl max-w-3xl mx-auto leading-relaxed text-white/90"
          >
            Building a sustainable future with environmentally responsible aluminum production 
            and circular economy principles.
          </motion.p>
        </div>
      </section>

      {/* Sustainability Pillars */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <motion.h2 variants={fadeInUp} className="text-4xl font-bold mb-6">
              Our Sustainability Pillars
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-xl text-muted-foreground max-w-3xl mx-auto">
              We are committed to sustainable production practices that protect the environment 
              and create value for future generations.
            </motion.p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {sustainabilityPillarsEn.map((pillar, index) => (
              <motion.div
                key={index}
                variants={staggerItem}
                className="bg-card p-8 rounded-lg border hover:shadow-lg transition-shadow duration-300 text-center"
              >
                <div className="mb-6 flex justify-center">
                  {pillar.icon}
                </div>
                <h3 className="text-xl font-semibold mb-4">{pillar.title}</h3>
                <p className="text-muted-foreground leading-relaxed">
                  {pillar.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Detailed Initiatives */}
      <section className="py-24 bg-secondary/30">
        <div className="container mx-auto px-4">
          <div className="flex flex-col space-y-24">
            {initiativesEn.map((item, index) => (
              <div 
                key={index} 
                className={`flex flex-col lg:flex-row items-center gap-12 ${index % 2 !== 0 ? 'lg:flex-row-reverse' : ''}`}
              >
                <motion.div 
                  initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  className="flex-1"
                >
                  <h2 className="text-3xl font-bold mb-6">{item.title}</h2>
                  <p className="text-lg text-muted-foreground leading-relaxed mb-8">
                    {item.content}
                  </p>
                  <div className="flex items-center gap-4 text-primary font-semibold">
                    <ShieldCheck className="w-6 h-6" />
                    <span>ISO 14001 and LEED Standards Full Compliance</span>
                  </div>
                </motion.div>
                <motion.div 
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  className="flex-1 w-full"
                >
                  <div className="relative rounded-2xl overflow-hidden shadow-2xl aspect-video">
                    <img 
                      src={item.image} 
                      alt={item.title} 
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                </motion.div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Environmental Impact */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-6">Environmental Impact</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Measurable results of our sustainability initiatives and environmental protection efforts.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                value: "95%",
                label: "Scrap Recovery Rate",
                description: "High-efficiency aluminum recycling"
              },
              {
                value: "30%",
                label: "Energy Reduction",
                description: "Achieved through smart automation"
              },
              {
                value: "100%",
                label: "Recyclable Material",
                description: "Aluminum's infinite recyclability"
              },
              {
                value: "Zero",
                label: "Water Waste Target",
                description: "Closed-loop treatment systems"
              }
            ].map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className="text-center p-6 bg-card rounded-lg border"
              >
                <div className="text-4xl font-bold text-primary mb-2">{stat.value}</div>
                <div className="text-lg font-semibold mb-2">{stat.label}</div>
                <div className="text-sm text-muted-foreground">{stat.description}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Future Goals */}
      <section className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-6">Future Sustainability Goals</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Our roadmap for achieving even greater environmental responsibility and sustainable growth.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                year: "2027",
                title: "Carbon Neutral Operations",
                description: "Achieve carbon neutrality in all production processes through renewable energy and efficiency improvements."
              },
              {
                year: "2028",
                title: "Zero Waste to Landfill",
                description: "Implement comprehensive waste management systems to eliminate landfill waste completely."
              },
              {
                year: "2030",
                title: "Circular Economy Leadership",
                description: "Become a regional leader in circular economy practices with 100% sustainable supply chain."
              }
            ].map((goal, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className="bg-card p-8 rounded-lg border hover:shadow-lg transition-shadow"
              >
                <div className="text-2xl font-bold text-primary mb-4">{goal.year}</div>
                <h3 className="text-xl font-semibold mb-4">{goal.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{goal.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="text-4xl font-bold mb-6">
              Partner with Us for Sustainable Solutions
            </h2>
            <p className="text-xl mb-8 text-primary-foreground/90">
              Join us in building a more sustainable future with environmentally responsible aluminum solutions. 
              Contact us to learn more about our sustainability initiatives.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" variant="secondary" className="text-lg px-8 py-6">
                <a href={ROUTE_PATHS.CONTACT_EN}>
                  Contact Us <ArrowRight className="ml-2 w-5 h-5" />
                </a>
              </Button>
              <Button asChild size="lg" variant="outline" className="text-lg px-8 py-6 border-white/30 text-white hover:bg-white hover:text-primary">
                <a href={ROUTE_PATHS.PRODUCTION_EN}>
                  Our Production
                </a>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}